import {
  FieldResearch,
  LaboratoryAnalysis,
  QualityControlMethodOptions,
  QualityRegimeOptions,
  SamplingStandardOptions,
  colorOptions,
  correctionReasonOptions,
  indicationYesNoOptions,
  limitSymbolOptions,
  pumpTypeOptions,
  qualityControlMethodOptions,
  qualityRegimeOptions,
  samplingStandardOptions,
  statusQualityControlOptions,
} from "@nens/brostar/uploadapi-client";

import { addSchema } from "@/helpers/schema-utils";

const GAR_FIELD_SCHEMA_LIBRARY = {
  // Shared metadata fields
  projectNumber: addSchema<string>({ type: "string", title: "Projectnummer" }),
  broId: addSchema<string>({ type: "string", title: "BRO-ID" }),
  correctionReason: addSchema<string>({
    type: "string",
    title: "Correctiereden",
    enum: correctionReasonOptions.map((opt) => opt.value),
  }),
  qualityRegime: addSchema<QualityRegimeOptions>({
    type: "string",
    title: "Kwaliteitsregime",
    enum: qualityRegimeOptions.map((opt) => opt.value),
  }),
  samplingOperator: addSchema<string>({ type: "string", title: "Bemonsteringsoperator" }),
  samplingStandard: addSchema<SamplingStandardOptions>({
    type: "string",
    title: "Bemonsteringsprocedure",
    enum: samplingStandardOptions.map((opt) => opt.value),
  }),
  qualityControlMethod: addSchema<QualityControlMethodOptions>({
    type: "string",
    title: "Beoordelingsprocedure",
    enum: qualityControlMethodOptions.map((opt) => opt.value),
  }),
  responsibleLaboratoryKvk: addSchema<string>({
    type: "string",
    title: "Verantwoordelijk laboratorium",
  }),
  deliveryAccountableParty: addSchema<string>({ type: "string", title: "Bronhouder [KVK]" }),
  groundwaterMonitoringNets: addSchema<string[]>({
    type: "array",
    title: "Gmns",
    items: { type: "string" },
  }),

  // Edit sourcedocument fields
  objectIdAccountableParty: addSchema<string>({
    type: "string",
    title: "Object-ID bronhouder",
  }),
  gmwBroId: addSchema<string>({ type: "string", title: "Put-ID" }),
  tubeNumber: addSchema<string>({ type: "string", title: "Buisnummer" }),
  fieldResearch: addSchema<FieldResearch>({
    type: "object",
    title: "Veldonderzoek",
    required: [
      "samplingDateTime",
      "samplingStandard",
      "pumpType",
      "abnormalityInCooling",
      "abnormalityInDevice",
      "pollutedByEngine",
      "filterAerated",
      "groundWaterLevelDroppedTooMuch",
      "abnormalFilter",
      "sampleAerated",
      "hoseReused",
      "temperatureDifficultToMeasure",
    ],
    properties: {
      samplingDateTime: {
        type: "string",
        title: "Bemonsteringsdatum/-tijd",
        format: "date-time",
      },
      samplingOperator: { type: "string", title: "Bemonsteringsoperator" },
      samplingStandard: { type: "string", title: "Bemonsteringsprocedure" },
      pumpType: {
        type: "string",
        title: "Pomptype",
        enum: pumpTypeOptions.map((opt) => opt.value),
      },
      primaryColour: {
        type: "string",
        title: "Primaire kleur",
        enum: colorOptions.map((opt) => opt.value),
      },
      secondaryColour: {
        type: "string",
        title: "Secundaire kleur",
        enum: colorOptions.map((opt) => opt.value),
      },
      colourStrength: {
        type: "string",
        title: "Kleursterkte",
        enum: colorOptions.map((opt) => opt.value),
      },
      abnormalityInCooling: {
        type: "string",
        title: "Bijzonderheid in koeling",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      abnormalityInDevice: {
        type: "string",
        title: "Bijzonderheid aan apparaat",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      pollutedByEngine: {
        type: "string",
        title: "Verontreinigd door motor",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      filterAerated: {
        type: "string",
        title: "Filter belucht",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      groundWaterLevelDroppedTooMuch: {
        type: "string",
        title: "Grondwaterstand te veel gedaald",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      abnormalFilter: {
        type: "string",
        title: "Abnormaal filter",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      sampleAerated: {
        type: "string",
        title: "Monster belucht",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      hoseReused: {
        type: "string",
        title: "Slang hergebruikt",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      temperatureDifficultToMeasure: {
        type: "string",
        title: "Temperatuur moeilijk te meten",
        enum: indicationYesNoOptions.map((opt) => opt.value),
      },
      fieldMeasurements: {
        type: "array",
        title: "Veldmetingen",
        items: {
          type: "object",
          required: ["parameter", "unit", "fieldMeasurementValue", "qualityControlStatus"],
          properties: {
            parameter: { type: "number", title: "Parameter" },
            unit: { type: "string", title: "Eenheid" },
            fieldMeasurementValue: { type: "number", title: "Meetwaarde" },
            qualityControlStatus: {
              type: "string",
              title: "Kwaliteitscontrolestatus",
              enum: statusQualityControlOptions.map((opt) => opt.value),
            },
          },
        },
      },
    },
  }),
  laboratoryAnalyses: addSchema<LaboratoryAnalysis[]>({
    type: "array",
    title: "Laboratoriumanalyse",
    items: {
      type: "object",
      title: "Laboratoriumanalyse",
      required: ["analysisProcesses"],
      properties: {
        responsibleLaboratoryKvk: { type: "string", title: "KVK laboratorium" },
        analysisProcesses: {
          type: "array",
          title: "Analyseprocessen",
          items: {
            type: "object",
            title: "Analyseproces",
            required: ["date", "analyses"],
            properties: {
              date: { type: "string", title: "Datum", format: "date" },
              analyticalTechnique: { type: "string", title: "Analysetechniek" },
              validationMethod: { type: "string", title: "Waarderingsmethode" },
              analyses: {
                type: "array",
                title: "Analyses",
                items: {
                  type: "object",
                  title: "Analyse",
                  required: ["parameter", "unit", "qualityControlStatus"],
                  properties: {
                    parameter: { type: "number", title: "Parameter" },
                    unit: { type: "string", title: "Eenheid" },
                    analysisMeasurementValue: { type: "number", title: "Meetwaarde" },
                    limitSymbol: {
                      type: "string",
                      title: "Grenswaarde symbool",
                      enum: limitSymbolOptions.map((opt) => opt.value),
                    },
                    reportingLimit: {
                      type: "number",
                      title: "Rapportagegrens",
                    },
                    qualityControlStatus: {
                      type: "string",
                      title: "Kwaliteitscontrolestatus",
                      enum: statusQualityControlOptions.map((opt) => opt.value),
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  }),

  // File fields
  fieldworkFile: addSchema<Blob>({
    type: "string",
    format: "data-url",
    title: "Veldwerkbestand (Excel/csv)",
  }),
  labFile: addSchema<Blob>({
    type: "string",
    format: "data-url",
    title: "Labbestand (Excel/csv)",
  }),
};

type GarFieldSchemaLibraryType = typeof GAR_FIELD_SCHEMA_LIBRARY;

export function pickGarLibraryFields<K extends keyof GarFieldSchemaLibraryType>(
  fieldNames: readonly K[]
): Pick<GarFieldSchemaLibraryType, K> {
  const result = {} as Pick<GarFieldSchemaLibraryType, K>;
  for (const name of fieldNames) {
    result[name] = GAR_FIELD_SCHEMA_LIBRARY[name];
  }
  return result;
}
